import {Pressable, View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'

import {atoms as a, useTheme} from '#/alf'
import {Text} from '#/components/Typography'
import {useSemanticTokens} from '#/wsocial/service/design-system'

/**
 * Scope of trends to load. Mirrors the `scope` query parameter on
 * `GET /trends` defined in `trends-how-it-works.md`.
 */
export type TrendsScope = 'global' | 'region' | 'foryou'

/**
 * Three-tab strip rendered directly below the search bar on `/search`.
 *
 * Mirrors the "Toggle Trends Menu" frame in Figma file
 * `yYh1MBotDu5Gm1KpL8EZxH` (W Social — Product — Trends), node `807:5943`.
 *
 * The middle tab shows the selected country's name (`regionLabel`, e.g.
 * "Sweden") and falls back to the generic "Regional" until a region is known.
 * Geolocation isn't wired yet, so the region defaults to Sweden upstream.
 */
export function TrendsScopeTabs({
  selected,
  onSelect,
  regionLabel,
}: {
  selected: TrendsScope
  onSelect: (scope: TrendsScope) => void
  /** Selected country name for the region tab; falls back to "Regional". */
  regionLabel?: string
}) {
  const {_} = useLingui()
  const ss = useSemanticTokens()

  const items: {scope: TrendsScope; label: string}[] = [
    {scope: 'global', label: _(msg`Global`)},
    {scope: 'region', label: regionLabel ?? _(msg`Regional`)},
    {scope: 'foryou', label: _(msg`For you`)},
  ]

  return (
    <View
      accessibilityRole="tablist"
      style={[
        a.w_full,
        a.flex_row,
        a.justify_end,
        a.align_center,
        a.border_b,
        {borderColor: ss.border.contrast_low},
      ]}>
      {items.map(item => (
        <TrendsScopeTab
          key={item.scope}
          label={item.label}
          isActive={item.scope === selected}
          onPress={() => onSelect(item.scope)}
        />
      ))}
    </View>
  )
}

function TrendsScopeTab({
  label,
  isActive,
  onPress,
}: {
  label: string
  isActive: boolean
  onPress: () => void
}) {
  const t = useTheme()

  return (
    <Pressable
      accessibilityRole="tab"
      accessibilityState={{selected: isActive}}
      onPress={onPress}
      style={[a.flex_1, a.align_center, a.pt_xl, a.gap_md]}>
      <Text
        style={[
          a.text_md,
          a.font_medium,
          isActive ? t.atoms.text : t.atoms.text_contrast_medium,
        ]}>
        {label}
      </Text>
      <View
        style={[
          a.w_full,
          {
            maxWidth: 64,
            height: 2,
            backgroundColor: isActive ? t.atoms.text.color : 'transparent',
          },
        ]}
      />
    </Pressable>
  )
}
